import { Link } from 'react-router-dom';
import HomeHeader from '../components/layout/HomeHeader';
import {
  Box,
  Button,
  Typography,
  Container,
  Paper,
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import TrophyIcon from '@mui/icons-material/EmojiEvents';
import SparklesIcon from '@mui/icons-material/AutoAwesome';
import SchoolIcon from '@mui/icons-material/School';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { withMarketingParams } from '../services/googleAds';

const APP_NAME = 'CPAx Academy';

const FOREST = '#0c200f';
const FOREST_MID = '#1a4328';
const PRIMARY = '#559534';
const PRIMARY_LIGHT = '#7cb663';
const SECONDARY = '#cc5f20';

export default function Home() {
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'surface.subtle' }}>
      <HomeHeader />

      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${FOREST} 0%, ${FOREST_MID} 55%, #0d3d1c 100%)`,
          py: { xs: 10, md: 15 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Grid pattern */}
        <Box sx={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          backgroundImage: `
            linear-gradient(${PRIMARY_LIGHT}0a 1px, transparent 1px),
            linear-gradient(90deg, ${PRIMARY_LIGHT}0a 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
          pointerEvents: 'none',
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ maxWidth: '820px', mx: 'auto', textAlign: 'center' }}>
            <Typography
              variant="h2"
              fontWeight={800}
              gutterBottom
              sx={{
                color: 'white',
                fontSize: { xs: '2.75rem', md: '4.25rem' },
                lineHeight: 1.1,
                letterSpacing: '-0.025em',
              }}
            >
              CPE Compliance Without the Guesswork
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(255,255,255,0.6)',
                mb: 6,
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                fontWeight: 400,
                lineHeight: 1.65,
              }}
            >
              No more wondering if your team has met their CPE requirements. CPAx Academy
              tracks every hour in real time and shows you exactly where each person stands -
              all NASBA-approved, all in one place.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                component={Link}
                to={withMarketingParams('/auth/individual-signup')}
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  px: 4, py: 1.75,
                  fontSize: '1rem', fontWeight: 700,
                  bgcolor: PRIMARY,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.24), 0 1px 2px rgba(0,0,0,0.16)',
                  borderRadius: '50px', textTransform: 'none',
                  '&:hover': {
                    bgcolor: PRIMARY_LIGHT,
                    boxShadow: '0 3px 8px rgba(0,0,0,0.20), 0 1px 3px rgba(0,0,0,0.12)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                }}
              >
                Get Started: Individual
              </Button>
              <Button
                component={Link}
                to={withMarketingParams('/auth/business-plans')}
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  px: 4, py: 1.75,
                  fontSize: '1rem', fontWeight: 700,
                  bgcolor: SECONDARY,
                  color: '#fffdf8',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.24), 0 1px 2px rgba(0,0,0,0.16)',
                  borderRadius: '50px', textTransform: 'none',
                  '&:hover': {
                    bgcolor: '#e89558',
                    boxShadow: '0 3px 8px rgba(0,0,0,0.20), 0 1px 3px rgba(0,0,0,0.12)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                }}
              >
                Get Started: Business
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h3" fontWeight={800} sx={{
            color: FOREST, mt: 0.5, mb: 2,
            fontSize: { xs: '2rem', md: '2.75rem' },
            letterSpacing: '-0.025em',
          }}>
            Why Choose {APP_NAME}?
          </Typography>
        </Box>

        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: 3,
        }}>
          {[
            { icon: <CheckCircleIcon />, color: PRIMARY, title: 'Team Visibility', desc: 'See where every team member stands, all in one dashboard.' },
            { icon: <SecurityIcon />, color: '#d97706', title: 'NASBA Approved', desc: 'All courses meet NASBA QAS requirements and state board regulations for CPE credits.' },
            { icon: <TrophyIcon />, color: '#7c3aed', title: 'Real-Time Progress Tracking', desc: 'Monitor CPE hours as you go and download certificates instantly upon completion.' },
            { icon: <MenuBookIcon />, color: PRIMARY, title: 'Extensive Course Library', desc: 'Hundreds of CPE courses covering audit, tax, ethics, and technology.' },
            { icon: <SparklesIcon />, color: '#14b8a6', title: 'Matched to Your Needs', desc: 'Courses tailored to your role and requirements, at your own pace, with interactive quizzes and real-world case studies along the way.' },
            { icon: <SchoolIcon />, color: '#ef4444', title: 'Expert Instructors', desc: 'Learn from industry experts and seasoned professionals with real-world experience.' },
          ].map((feature, idx) => (
            <Paper
              key={idx}
              elevation={0}
              sx={{
                p: 3.5,
                border: '1px solid rgba(0,0,0,0.07)',
                borderRadius: 3,
                bgcolor: 'white',
                transition: 'all 0.28s ease',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: `0 20px 60px rgba(0,0,0,0.10)`,
                  borderColor: `${feature.color}35`,
                  '& .icon-bg': { bgcolor: `${feature.color}20` },
                },
              }}
            >
              <Box
                className="icon-bg"
                sx={{
                  width: 54, height: 54, borderRadius: 2.5,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: `${feature.color}12`, mb: 2.5,
                  transition: 'background 0.28s ease',
                  '& svg': { fontSize: 26, color: feature.color },
                }}
              >
                {feature.icon}
              </Box>
              <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: FOREST, fontSize: '1.05rem' }}>
                {feature.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                {feature.desc}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Container>

      {/* CTA Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Box sx={{
          background: `linear-gradient(135deg, ${FOREST} 0%, ${FOREST_MID} 100%)`,
          borderRadius: 4,
          p: { xs: 4, md: 6 },
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: `0 30px 80px ${FOREST}40`,
        }}>
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <TrophyIcon sx={{
              fontSize: 72, color: SECONDARY, mb: 2,
            }} />
            <Typography variant="h4" fontWeight={800} gutterBottom sx={{ color: 'white', letterSpacing: '-0.02em' }}>
              Start Learning Today
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: 'rgba(255,255,255,0.62)', lineHeight: 1.65 }}>
              Join thousands of CPAs who trust our platform for their continuing education needs.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
              <Button
                component={Link}
                to={withMarketingParams('/auth/individual-signup')}
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  px: 4, py: 1.5, whiteSpace: 'nowrap',
                  fontWeight: 700, fontSize: '1rem',
                  bgcolor: PRIMARY,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.24), 0 1px 2px rgba(0,0,0,0.16)',
                  borderRadius: '50px', textTransform: 'none',
                  '&:hover': {
                    bgcolor: PRIMARY_LIGHT,
                    boxShadow: '0 3px 8px rgba(0,0,0,0.20), 0 1px 3px rgba(0,0,0,0.12)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                }}
              >
                Get Started: Individual
              </Button>
              <Button
                component={Link}
                to={withMarketingParams('/auth/business-plans')}
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  px: 4, py: 1.5, whiteSpace: 'nowrap',
                  fontWeight: 700, fontSize: '1rem',
                  bgcolor: SECONDARY,
                  color: '#fffdf8',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.24), 0 1px 2px rgba(0,0,0,0.16)',
                  borderRadius: '50px', textTransform: 'none',
                  '&:hover': {
                    bgcolor: '#e89558',
                    boxShadow: '0 3px 8px rgba(0,0,0,0.20), 0 1px 3px rgba(0,0,0,0.12)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                }}
              >
                Get Started: Business
              </Button>
            </Box>
          </Box>
        </Box>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          bgcolor: FOREST,
          color: 'rgba(255,255,255,0.45)',
          py: 4,
          mt: 'auto',
          borderTop: `1px solid rgba(255,255,255,0.07)`,
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" align="center">
            &copy; {new Date().getFullYear()} CPAx Academy. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}
